import React, { useState, useMemo } from 'react';
import ClubSearch from '../shared/ClubSearch';

const FilterBarContent = ({ filters, handleFilterChange, handleReset, organizers, filteredClubs, clubs, isMobile }) => {
    return (
        <div className={`grid grid-cols-1 ${!isMobile ? 'md:grid-cols-2 lg:grid-cols-6' : ''} gap-3`}>
            {/* Filtro de Nivel */}
            <div className="relative">
                <label
                    htmlFor="filter-level"
                    className="block text-xs font-semibold text-gray-600 mb-1"
                >
                    🎯 Nivel
                </label>
                <select
                    id="filter-level"
                    className="
          w-full bg-gray-50 border-2 border-gray-200 rounded-lg
          px-4 py-2.5 text-sm
          focus:border-primary-500 focus:ring-2 focus:ring-primary-200
          focus:outline-none transition-all cursor-pointer
        "
                    value={filters.level}
                    onChange={(e) => handleFilterChange('level', e.target.value)}
                >
                    <option value="">Todos</option>
                    <option value="1.0-2.0">Iniciación (1.0-2.0)</option>
                    <option value="2.0-3.0">Intermedio (2.0-3.0)</option>
                    <option value="3.0-4.0">Avanzado (3.0-4.0)</option>
                    <option value="4.0-5.0">Experto (4.0-5.0)</option>
                    <option value="5.0-7.0">Competición (5.0+)</option>
                </select>
            </div>

            {/* Filtro de Ciudad */}
            <div className="relative">
                <label
                    htmlFor="filter-city"
                    className="block text-xs font-semibold text-gray-600 mb-1"
                >
                    📍 Ciudad
                </label>
                <select
                    id="filter-city"
                    className="
          w-full bg-gray-50 border-2 border-gray-200 rounded-lg
          px-4 py-2.5 text-sm
          focus:border-primary-500 focus:ring-2 focus:ring-primary-200
          focus:outline-none transition-all cursor-pointer
        "
                    value={filters.city}
                    onChange={(e) => handleFilterChange('city', e.target.value)}
                >
                    <option value="">Todas</option>
                    {SPANISH_CITIES.map((city) => (
                        <option key={city} value={city.toLowerCase().replace(/ /g, '_').normalize("NFD").replace(/[\u0300-\u036f]/g, "")}>
                            {city}
                        </option>
                    ))}
                </select>
            </div>

            {/* Filtro de Organizador */}
            <div className="relative">
                <label
                    htmlFor="filter-organizer"
                    className="block text-xs font-semibold text-gray-600 mb-1"
                >
                    👤 Organizador
                </label>
                <select
                    id="filter-organizer"
                    className="
          w-full bg-gray-50 border-2 border-gray-200 rounded-lg
          px-4 py-2.5 text-sm
          focus:border-primary-500 focus:ring-2 focus:ring-primary-200
          focus:outline-none transition-all cursor-pointer truncate
        "
                    value={filters.organizerId}
                    onChange={(e) => handleFilterChange('organizerId', e.target.value)}
                >
                    <option value="">Todos</option>
                    {organizers.map((org) => (
                        <option key={org.id} value={org.id}>
                            {org.firstName} {org.lastName}
                        </option>
                    ))}
                </select>
            </div>

            {/* Filtro de Club (Borrador Nacional) */}
            <div className="relative">
                <label
                    className="block text-xs font-semibold text-gray-600 mb-1"
                >
                    🏟️ Búsqueda por Club
                </label>
                <ClubSearch
                    onSelect={(club) => handleFilterChange('clubName', club ? club.n : '')}
                    placeholder="Escribe el nombre del club..."
                    initialValue={filters.clubName || ''}
                    className="!py-2 !text-sm"
                />
            </div>

            {/* Filtro de Fecha */}
            <div className="relative">
                <label
                    htmlFor="filter-date"
                    className="block text-xs font-semibold text-gray-600 mb-1"
                >
                    📅 Fecha
                </label>
                <input
                    type="date"
                    id="filter-date"
                    className="
          w-full bg-gray-50 border-2 border-gray-200 rounded-lg
          px-4 py-2.5 text-sm
          focus:border-primary-500 focus:ring-2 focus:ring-primary-200
          focus:outline-none transition-all cursor-pointer
        "
                    value={filters.date === 'all' ? '' : filters.date}
                    onChange={(e) => handleFilterChange('date', e.target.value)}
                />
            </div>

            {/* Botón Limpiar Filtros */}
            <div className="flex items-end">
                <button
                    onClick={handleReset}
                    className="
          w-full bg-gray-100 hover:bg-gray-200 text-gray-700
          font-semibold py-2.5 px-4 rounded-lg
          transition-colors duration-200
          flex items-center justify-center gap-2
        "
                >
                    <span>🔄</span>
                    <span className={!isMobile ? "md:hidden lg:inline" : ""}>Limpiar</span>
                </button>
            </div>
        </div>
    );
};

const FilterBar = ({ onFilterChange, initialFilters = {}, organizers = [], clubs = [], isMobileView = false }) => {
    const [filters, setFilters] = useState({
        level: initialFilters.level || '',
        city: initialFilters.city || '',
        date: initialFilters.date || 'all',
        organizerId: initialFilters.organizerId || '',
        clubId: initialFilters.clubId || '',
        ...initialFilters
    });

    // Sync internal state when initialFilters changes (e.g. from SearchHero)
    React.useEffect(() => {
        setFilters(prev => ({
            ...prev,
            ...initialFilters
        }));
    }, [initialFilters]);

    const filteredClubs = useMemo(() => {
        if (!filters.organizerId) return clubs;
        return clubs.filter(club => club.organizerId === filters.organizerId || (club.organizer && club.organizer.id === filters.organizerId));
    }, [clubs, filters.organizerId]);

    const handleFilterChange = (filterName, value) => {
        const newFilters = {
            ...filters,
            [filterName]: value
        };
        if (filterName === 'organizerId') newFilters.clubId = '';
        setFilters(newFilters);
        if (onFilterChange) onFilterChange(newFilters);
    };

    const handleReset = () => {
        const resetFilters = { level: '', city: '', date: 'all', organizerId: '', clubId: '', clubName: '' };
        setFilters(resetFilters);
        if (onFilterChange) onFilterChange(resetFilters);
    };

    if (isMobileView) {
        return (
            <FilterBarContent
                filters={filters}
                handleFilterChange={handleFilterChange}
                handleReset={handleReset}
                organizers={organizers}
                filteredClubs={filteredClubs}
                clubs={clubs}
                isMobile={true}
            />
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-card p-4 mb-6 sticky top-20 z-40 hidden md:block">
            <FilterBarContent
                filters={filters}
                handleFilterChange={handleFilterChange}
                handleReset={handleReset}
                organizers={organizers}
                filteredClubs={filteredClubs}
                clubs={clubs}
                isMobile={false}
            />

            {(filters.level || filters.city || filters.date !== 'all') && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-gray-600">
                            Filtros activos:
                        </span>

                        {filters.level && (
                            <span className="inline-flex items-center gap-1.5 bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-xs font-medium">
                                <span>🎯 {filters.level}</span>
                                <button
                                    onClick={() => handleFilterChange('level', '')}
                                    className="hover:bg-primary-200 rounded-full p-0.5 transition-colors"
                                >
                                    ×
                                </button>
                            </span>
                        )}

                        {filters.city && (
                            <span className="inline-flex items-center gap-1.5 bg-accent-100 text-accent-700 px-3 py-1 rounded-full text-xs font-medium">
                                <span>📍 {filters.city.charAt(0).toUpperCase() + filters.city.slice(1)}</span>
                                <button
                                    onClick={() => handleFilterChange('city', '')}
                                    className="hover:bg-accent-200 rounded-full p-0.5 transition-colors"
                                >
                                    ×
                                </button>
                            </span>
                        )}

                        {filters.date !== 'all' && (
                            <span className="inline-flex items-center gap-1.5 bg-success/10 text-success px-3 py-1 rounded-full text-xs font-medium">
                                <span>📅 {new Date(filters.date).toLocaleDateString()}</span>
                                <button
                                    onClick={() => handleFilterChange('date', 'all')}
                                    className="hover:bg-success/20 rounded-full p-0.5 transition-colors"
                                >
                                    ×
                                </button>
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Variante móvil compacta (Floating Button + Modal)
// Variante móvil intuitiva (Botón Inline Expandible)
FilterBar.Mobile = ({ onFilterChange, initialFilters = {}, organizers = [], clubs = [] }) => {
    const [isOpen, setIsOpen] = useState(false);

    // Contar filtros activos para mostrar "badge"
    const activeFiltersCount = [
        initialFilters.level,
        initialFilters.city,
        initialFilters.organizerId,
        initialFilters.clubId,
        initialFilters.clubName,
        initialFilters.date !== 'all'
    ].filter(Boolean).length;

    return (
        <div className="md:hidden mb-6">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`
                    w-full py-3 px-4 rounded-xl flex items-center justify-between
                    font-bold shadow-sm border
                    transition-all duration-200
                    ${isOpen
                        ? 'bg-primary-500 text-white border-primary-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}
                `}
            >
                <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    <span>{isOpen ? 'Ocultar Filtros' : 'Filtrar Pozos'}</span>
                </div>

                <div className="flex items-center gap-2">
                    {activeFiltersCount > 0 && (
                        <span className={`
                            text-xs font-bold px-2 py-1 rounded-full
                            ${isOpen ? 'bg-white text-primary-600' : 'bg-primary-100 text-primary-700'}
                        `}>
                            {activeFiltersCount}
                        </span>
                    )}

                    <svg
                        className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </button>

            {/* Panel de filtros expandible */}
            <div className={`
                overflow-hidden transition-all duration-300 ease-in-out
                ${isOpen ? 'max-h-[1000px] opacity-100 mt-3' : 'max-h-0 opacity-0'}
            `}>
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
                    <FilterBar
                        onFilterChange={onFilterChange}
                        initialFilters={initialFilters}
                        organizers={organizers}
                        clubs={clubs}
                        isMobileView={true}
                    />
                </div>
            </div>
        </div>
    );
};

export const SPANISH_CITIES = [
    "A Coruña", "Adeje", "Aguilas", "Albacete", "Alcalá de Henares", "Alcobendas", "Alcorcón", "Alcoy", "Algeciras", "Alicante", "Almería", "Alzira", "Aranjuez", "Arganda del Rey", "Arona", "Arrecife", "Avilés",
    "Badajoz", "Badalona", "Barakaldo", "Barcelona", "Benalmádena", "Benidorm", "Bilbao", "Boadilla del Monte", "Burgos",
    "Cáceres", "Cádiz", "Calvià", "Cartagena", "Castellón de la Plana", "Castelldefels", "Cerdanyola del Vallès", "Ceuta", "Chiclana de la Frontera", "Ciudad Real", "Collado Villalba", "Córdoba", "Cornellà de Llobregat", "Coslada", "Cuenca",
    "Dos Hermanas", "Eibar", "El Ejido", "El Prat de Llobregat", "El Puerto de Santa María", "Elche", "Elda", "Estepona",
    "Ferrol", "Figueres", "Fuengirola", "Fuenlabrada", "Gandia", "Getafe", "Gijón", "Girona", "Granada", "Granollers", "Guadalajara",
    "Hospitalet de Llobregat", "Huelva", "Huesca", "Ibiza", "Irún",
    "Jaén", "Jerez de la Frontera",
    "Las Palmas de Gran Canaria", "Las Rozas de Madrid", "Leganés", "León", "Lleida", "Logroño", "Lorca", "Lugo",
    "Madrid", "Majadahonda", "Málaga", "Manresa", "Marbella", "Mataró", "Melilla", "Mérida", "Mijas", "Molina de Segura", "Mollet del Vallès", "Móstoles", "Motril", "Murcia",
    "Orihuela", "Ourense", "Oviedo",
    "Palencia", "Palma", "Pamplona", "Parla", "Paterna", "Pinto", "Plasencia", "Ponferrada", "Pontevedra", "Portugalete", "Pozuelo de Alarcón", "Puerto del Rosario",
    "Reus", "Rivas-Vaciamadrid", "Roquetas de Mar", "Rubí",
    "Sabadell", "Sagunto", "Salamanca", "San Bartolomé de Tirajana", "San Cristóbal de La Laguna", "San Fernando", "San Sebastián", "San Sebastián de los Reyes", "Sanlúcar de Barrameda", "Sant Boi de Llobregat", "Sant Cugat del Vallès", "Santa Coloma de Gramenet", "Santa Cruz de Tenerife", "Santa Lucía de Tirajana", "Santander", "Santiago de Compostela", "Segovia", "Sevilla", "Siero", "Soria",
    "Talavera de la Reina", "Tarragona", "Telde", "Terrassa", "Teruel", "Toledo", "Torrejón de Ardoz", "Torrelavega", "Torremolinos", "Torrevieja", "Torrent",
    "Valdemoro", "Valencia", "Valladolid", "Vélez-Málaga", "Vigo", "Viladecans", "Vilanova i la Geltrú", "Vila-real", "Vitoria-Gasteiz",
    "Zamora", "Zaragoza"
];

export default FilterBar;
